import { useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import type { AssignablePerson, Board, BoardColumn, Grant, Priority, TaskGroup, WorkItemCard, WorkItemType } from '../lib/types';
import { WORK_ITEM_TYPE_GLYPH, WORK_ITEM_TYPE_LABELS, WORK_STAGE_LABELS } from '../lib/types';
import { AssigneePicker } from '../components/Assignees';
import { BillingBadge } from '../components/Badges';
import { SlaBadge } from '../components/SlaBadge';
import { AddCardForm } from './AddCardForm';
import { DateCell } from '../components/DateCell';

interface Props {
  board: Board;
  people: AssignablePerson[];
  onOpen: (item: WorkItemCard) => void;
  onChanged: () => void | Promise<void>;
  onError: (message: string | null) => void;
  /** Set while the board is filtered: sections with nothing in them are dropped. */
  hideEmptySections?: boolean;
}

/**
 * The task list read by section rather than by status — the view Chris's board opens on, and
 * the one that scales: sixty tasks in six named phases is legible, sixty in one flat list is
 * not. Every cell edits in place, because opening a drawer to change one due date is friction
 * nobody accepts twice.
 */
export function TaskTable({ board, people, onOpen, onChanged, onError, hideEmptySections }: Props) {
  const [adding, setAdding] = useState<string | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const canWrite = board.capabilities['work.write'] !== 'none';
  /**
   * Raising work on the board is ours. Asked by ROLE, not by borrowing a staff capability —
   * see `canAddCards` on the board page, which this mirrors so the two views of one board
   * cannot offer different things. A customer's route in is "Report it".
   */
  const isClient = board.role === 'client' || board.role === 'client_admin';
  const canAdd = canWrite && !isClient;
  /** Dates are a planning field: ours to set, theirs to read. See `work.plan`. */
  const canPlan = board.capabilities['work.plan'] !== 'none';
  /** Bug or feature — warranty against billable. Their lead holds it; everybody else does not. */
  const canClassify = board.capabilities['work.classify'] !== 'none';
  /** Changing a status here is the same act as dragging the card. See `work.move`. */
  const canMove = board.capabilities['work.move'] !== 'none';
  const assignGrant = board.capabilities['work.assign'];
  const canConfigure = board.capabilities['project.manage'] === 'full';
  const statuses = board.columns;

  const sections: Array<TaskGroup | { id: null; name: string; color: string; collapsed: false; items: WorkItemCard[] }> = [
    ...board.groups,
  ];
  if (board.ungrouped.length > 0) {
    sections.push({ id: null, name: 'Unfiled', color: '#7A7A84', collapsed: false, items: board.ungrouped });
  }

  /**
   * While a filter is on, a section with no matches is noise — six headers reading "0 tasks"
   * bury the one that matched. With no filter they stay, because an empty section is still
   * somewhere to add work.
   */
  const visible = hideEmptySections ? sections.filter((section) => section.items.length > 0) : sections;

  return (
    <>
      {visible.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>No sections yet</h3>
            <p>A section is a phase of the work — Planning, Build, Launch. Tasks live inside them.</p>
            {canConfigure && (
              <button type="button" className="btn btn-primary mt-2" onClick={() => setCreatingGroup(true)}>
                Add the first section
              </button>
            )}
          </div>
        </div>
      ) : (
        visible.map((section) => (
          <Section
            key={section.id ?? 'unfiled'}
            section={section}
            statuses={statuses}
            people={people}
            canAdd={canAdd}
            canPlan={canPlan}
            canClassify={canClassify}
            canMove={canMove}
            assignGrant={assignGrant}
            canConfigure={canConfigure}
            projectId={board.project.id}
            adding={adding === (section.id ?? 'unfiled')}
            onAdd={() => setAdding(section.id ?? 'unfiled')}
            onCancelAdd={() => setAdding(null)}
            onOpen={onOpen}
            onChanged={onChanged}
            onError={onError}
          />
        ))
      )}

      {canConfigure &&
        (creatingGroup ? (
          <NewSectionForm
            projectId={board.project.id}
            onCancel={() => setCreatingGroup(false)}
            onCreated={async () => {
              setCreatingGroup(false);
              await onChanged();
            }}
            onError={onError}
          />
        ) : (
          sections.length > 0 && (
            <button type="button" className="btn" onClick={() => setCreatingGroup(true)}>
              + New section
            </button>
          )
        ))}
    </>
  );
}

function Section({
  section,
  statuses,
  people,
  canAdd,
  canPlan,
  canClassify,
  canMove,
  assignGrant,
  canConfigure,
  projectId,
  adding,
  onAdd,
  onCancelAdd,
  onOpen,
  onChanged,
  onError,
}: {
  section: TaskGroup | { id: null; name: string; color: string; collapsed: false; items: WorkItemCard[] };
  statuses: BoardColumn[];
  people: AssignablePerson[];
  /** Raising work here is ours; a customer uses "Report it". */
  canAdd: boolean;
  canPlan: boolean;
  canClassify: boolean;
  canMove: boolean;
  /** `none` hides the picker; `own` keeps it and greys our own people inside it. */
  assignGrant: Grant;
  canConfigure: boolean;
  projectId: string;
  adding: boolean;
  onAdd: () => void;
  onCancelAdd: () => void;
  onOpen: (item: WorkItemCard) => void;
  onChanged: () => void | Promise<void>;
  onError: (message: string | null) => void;
  /** Set while the board is filtered: sections with nothing in them are dropped. */
  hideEmptySections?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(section.collapsed);
  const ask = useAsk();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(section.name);

  const isUnfiled = section.id === null;

  async function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    // Remembered for the project, so a finished phase stays folded for everyone.
    if (!isUnfiled) {
      try {
        await api.patch(`/groups/${section.id}`, { collapsed: next });
      } catch {
        /* a failed preference is not worth interrupting anyone over */
      }
    }
  }

  async function rename() {
    if (!name.trim() || isUnfiled) return;
    try {
      await api.patch(`/groups/${section.id}`, { name: name.trim() });
      setRenaming(false);
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not rename that section');
    }
  }

  async function remove() {
    if (isUnfiled) return;
    const sure = await ask.confirm({
      title: `Delete the section “${section.name}”?`,
      body: 'Its tasks are kept — they become unfiled rather than being deleted with it.',
      confirmLabel: 'Delete the section',
      danger: true,
    });
    if (!sure) return;
    try {
      const result = await api.del<{ unfiled: number }>(`/groups/${section.id}`);
      onError(null);
      await onChanged();
      if (result.unfiled > 0) {
        onError(`Section deleted. ${result.unfiled} task${result.unfiled === 1 ? '' : 's'} moved to Unfiled.`);
      }
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not delete that section');
    }
  }

  return (
    <div className="group-block">
      <div className="group-head" style={{ borderLeftColor: section.color }} onClick={renaming ? undefined : toggle}>
        <span className="group-caret" aria-hidden>
          {collapsed ? '▶' : '▼'}
        </span>
        {renaming ? (
          <input
            type="text"
            value={name}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void rename();
              if (e.key === 'Escape') {
                setName(section.name);
                setRenaming(false);
              }
            }}
            style={{ width: 200, padding: '3px 7px' }}
          />
        ) : (
          <span className="group-name">{section.name}</span>
        )}
        <span className="dim" style={{ fontSize: 12 }}>
          {section.items.length} {section.items.length === 1 ? 'task' : 'tasks'}
        </span>

        {isUnfiled && (
          <span className="badge" title="These tasks are not in a section yet">
            no section
          </span>
        )}

        {canConfigure && !isUnfiled && !renaming && (
          <span className="row" style={{ marginLeft: 'auto', gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => setRenaming(true)}>
              Rename
            </button>
            <button type="button" className="btn btn-quiet btn-sm" onClick={remove}>
              Delete
            </button>
          </span>
        )}
        {renaming && (
          <span className="row" style={{ marginLeft: 'auto', gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn-primary btn-sm" onClick={rename}>
              Save
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => {
                setName(section.name);
                setRenaming(false);
              }}
            >
              Cancel
            </button>
          </span>
        )}
      </div>

      {!collapsed && (
        <>
          {section.items.length > 0 && (
            <div className="table-scroll">
              <table className="task-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 260 }}>Task</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Assignee</th>
                    {/*
                      * The stage and how far along, which this view had neither of. A task read
                      * "Developing" on the kanban and showed nothing at all here, so the two
                      * readings of the same board could not be compared.
                      */}
                    <th>Progress</th>
                    <th>SLA</th>
                    {/* A start as well as a due date, so a task can have a span the timeline can draw. */}
                    <th>Start</th>
                    <th>Due date</th>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map((item) => (
                    <Row
                      key={item.id}
                      item={item}
                      statuses={statuses}
                      people={people}
                      canPlan={canPlan}
                      canClassify={canClassify}
                      canMove={canMove}
                      assignGrant={assignGrant}
                      onOpen={onOpen}
                      onChanged={onChanged}
                      onError={onError}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {canAdd &&
            !isUnfiled &&
            (adding ? (
              <AddCardForm
                projectId={projectId}
                statusId={statuses[0]?.id ?? ''}
                groupId={section.id ?? undefined}
                people={people}
                onCancel={onCancelAdd}
                onAdded={() => void onChanged()}
              />
            ) : (
              <button type="button" className="board-add" style={{ width: '100%' }} onClick={onAdd}>
                + Add task
              </button>
            ))}
        </>
      )}
    </div>
  );
}

function Row({
  item,
  statuses,
  people,
  canPlan,
  canClassify,
  canMove,
  assignGrant,
  onOpen,
  onChanged,
  onError,
}: {
  item: WorkItemCard;
  statuses: BoardColumn[];
  people: AssignablePerson[];
  canPlan: boolean;
  /** Bug or feature. The customer's lead holds it; everybody else on their side does not. */
  canClassify: boolean;
  canMove: boolean;
  /** `none` hides the picker; `own` keeps it and greys our own people inside it. */
  assignGrant: Grant;
  onOpen: (item: WorkItemCard) => void;
  onChanged: () => void | Promise<void>;
  onError: (message: string | null) => void;
  /** Set while the board is filtered: sections with nothing in them are dropped. */
  hideEmptySections?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const ask = useAsk();
  /**
   * Two different questions, and this table used to ask only one of them.
   *
   *   movable     may they move it between columns — ours
   *   schedulable may they set the type, the priority, the size and the dates — ours
   *
   * Every cell was gated on "may they change the work at all", so a customer was shown live
   * dropdowns for status and priority that the API refuses. A control that cannot work should
   * not be a control: each cell falls back to the value as plain text.
   *
   * THE TYPE MOVED FROM THE FIRST GROUP TO THE SECOND. It read as a label the customer chose,
   * and it is not: bug or feature is the difference between warranty work and work somebody is
   * invoiced for. They still say what kind of thing it is when they raise it; what is refused
   * is reclassifying it afterwards.
   */
  const movable = canMove && item.editable;
  const schedulable = canPlan && item.editable;
  const status = statuses.find((s) => s.id === item.statusId);

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    onError(null);
    try {
      await api.patch(`/work-items/${item.id}`, body);
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not save that change');
    } finally {
      setBusy(false);
    }
  }

  /** Status goes through the move endpoint so the blocked and done rules still apply. */
  async function changeStatus(statusId: string) {
    const target = statuses.find((s) => s.id === statusId);
    if (!target) return;

    let reason: string | undefined;
    if (target.category === 'blocked') {
      const answer = await ask.prompt({
        title: `Why is ${item.key} blocked?`,
        body: 'Whoever picks this up next reads this, so say what it is waiting on.',
        label: 'What is holding it up',
        placeholder: 'Waiting on the customer to confirm the account numbers',
        confirmLabel: 'Mark it blocked',
        minLength: 3,
      });
      if (answer === null) return;
      if (!answer.trim()) {
        onError('A blocked task needs a reason.');
        return;
      }
      reason = answer.trim();
    }

    setBusy(true);
    onError(null);
    try {
      await api.put(`/work-items/${item.id}/move`, { statusId, position: target.items.length, reason });
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not change that status');
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr style={busy ? { opacity: 0.55 } : undefined}>
      <td>
        <button
          type="button"
          onClick={() => onOpen(item)}
          className="row"
          style={{ gap: 8, background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left' }}
        >
          <span className="key">{item.key}</span>
          <span>{item.title}</span>
          {item.blockedSince && <span className="badge badge-accent">blocked</span>}
          {item.closedAt && <span className="badge">closed</span>}
          <BillingBadge exception={item.billingException} />
          {item.reopenCount > 0 && <span className="badge badge-warn" title={`Reopened ${item.reopenCount} times`}>↻{item.reopenCount}</span>}
          {item.merge && <span className="badge badge-info">⑂ !{item.merge.iid}</span>}
        </button>
      </td>

      <td>
        {/*
          `work.classify`, which the customer's LEAD holds and nobody else on their side does.
          Bug or feature is the difference between warranty work and work somebody is invoiced
          for — too consequential to ride on `work.write`, which a plain client holds for their
          own requests. The API agrees, so this is not merely hiding a control that would work.
        */}
        {canClassify && item.editable ? (
          <select className="cell-select" value={item.type} disabled={busy} onChange={(e) => patch({ type: e.target.value as WorkItemType })}>
            {(Object.keys(WORK_ITEM_TYPE_LABELS) as WorkItemType[]).map((key) => (
              <option key={key} value={key}>
                {WORK_ITEM_TYPE_LABELS[key]}
              </option>
            ))}
          </select>
        ) : (
          <span className="dim">
            {WORK_ITEM_TYPE_GLYPH[item.type]} {WORK_ITEM_TYPE_LABELS[item.type]}
          </span>
        )}
      </td>

      <td>
        {movable ? (
          <select
            className="cell-select"
            value={item.statusId ?? ''}
            disabled={busy}
            onChange={(e) => changeStatus(e.target.value)}
            style={{
              background: status?.color ?? 'var(--surface-sunken)',
              color: status ? '#fff' : 'var(--text)',
              fontWeight: 600,
              fontSize: 11,
              border: 'none',
            }}
          >
            {!item.statusId && <option value="">No status</option>}
            {statuses.map((s) => (
              <option key={s.id} value={s.id} style={{ background: 'var(--surface)', color: 'var(--text)' }}>
                {s.label}
              </option>
            ))}
          </select>
        ) : status ? (
          <span className="status-pill" style={{ background: status.color }}>
            {status.label}
          </span>
        ) : (
          <span className="dim">—</span>
        )}
      </td>

      <td>
        {schedulable && !(item.impact && item.urgency) ? (
          <select className="cell-select" value={item.priority} disabled={busy} onChange={(e) => patch({ priority: e.target.value as Priority })}>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="none">No priority</option>
          </select>
        ) : (
          <span className="muted">{item.priority === 'none' ? '—' : item.priority}</span>
        )}
      </td>

      <td>
        <AssigneePicker
          value={item.assignees}
          people={people}
          // `none` takes the control away entirely; `own` keeps it and greys our own people
          // inside it, since a customer may move theirs and not ours.
          disabled={busy || assignGrant === 'none'}
          grant={assignGrant}
          compact
          onChange={(next) => patch({ assigneeIds: next.map((person) => person.id) })}
        />
      </td>

      <td>
        {item.stage || item.percentComplete !== null ? (
          <span className="row" style={{ gap: 6, alignItems: 'center' }}>
            {item.stage && <span className={`badge stage-${item.stage}`}>{WORK_STAGE_LABELS[item.stage]}</span>}
            {item.percentComplete !== null && (
              <span className="tabular-nums dim" style={{ fontSize: 12 }}>{item.percentComplete}%</span>
            )}
          </span>
        ) : (
          /* Nobody has said anything about this task — which is not the same as 0%. */
          <span className="dim">—</span>
        )}
      </td>

      <td>{item.sla ? <SlaBadge sla={item.sla} /> : <span className="dim">—</span>}</td>

      <td>
        <DateCell
          value={item.startDate}
          editable={schedulable && !busy}
          max={item.dueDate ?? undefined}
          onChange={(next) => patch({ startDate: next ?? undefined })}
        />
      </td>

      <td>
        <DateCell
          value={item.dueDate}
          editable={schedulable && !busy}
          emphasis={item.overdue}
          onChange={(next) => patch({ dueDate: next ?? undefined })}
        />
      </td>
    </tr>
  );
}

function NewSectionForm({
  projectId,
  onCancel,
  onCreated,
  onError,
}: {
  projectId: string;
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
  onError: (message: string | null) => void;
  /** Set while the board is filtered: sections with nothing in them are dropped. */
  hideEmptySections?: boolean;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    onError(null);
    try {
      await api.post(`/projects/${projectId}/groups`, { name: name.trim() });
      setName('');
      await onCreated();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not add that section');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card card-pad" onSubmit={submit}>
      <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
        <label className="field grow" style={{ marginBottom: 0 }}>
          <span className="field-label">Section name</span>
          <input
            type="text"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder="Planning, Build, Launch…"
            onKeyDown={(e) => e.key === 'Escape' && onCancel()}
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
          {busy ? 'Adding…' : 'Add section'}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
